import React, { useState, useEffect, useCallback, useRef } from 'react';
import Icon from './icons';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/** Same rules the server enforces (account-security.js passwordProblem). */
export function passwordChecks(username, pw) {
    const name = String(username || '').trim().toLowerCase();
    const low = String(pw || '').toLowerCase();
    return [
        { ok: low.length >= 8, label: 'At least 8 characters' },
        { ok: /[a-z]/i.test(pw || '') && /[0-9]/.test(pw || ''), label: 'Letters and numbers' },
        { ok: !!low && !(name && (low === name || (name.length >= 3 && low.includes(name)))), label: 'Not your account name' }
    ];
}

function Rules({ username, password }) {
    return (
        <ul style={{ listStyle: 'none', padding: 0, margin: '6px 0 0', fontSize: 12 }}>
            {passwordChecks(username, password).filter(c => username !== null || c.label !== 'Not your account name').map(c => (
                <li key={c.label} style={{ color: c.ok ? '#15803d' : '#94a3b8' }}>{c.ok ? '✓' : '○'} {c.label}</li>
            ))}
        </ul>
    );
}

async function call(path, { token, body, method = 'POST' } = {}) {
    try {
        const res = await fetch(`${API_URL}${path}`, {
            method,
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: body ? JSON.stringify(body) : undefined
        });
        return { ok: res.ok, status: res.status, body: await res.json().catch(() => ({})) };
    } catch (e) {
        return { ok: false, status: 0, body: { error: 'Connection error. Please try again.' } };
    }
}

const box = { maxWidth: 440 };
const note = (bg, color) => ({ background: bg, color, padding: '10px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 });

/**
 * Shows the dashboard only once the account is secured: verified email, then a
 * new password. Until then the reseller sees the two steps (the server refuses
 * every other request anyway).
 */
export function CpSecurityGate({ token, username, onToken, onLogout, children }) {
    const [status, setStatus] = useState(null);      // null = loading
    const [step, setStep] = useState('email');        // email | code | password
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [pw, setPw] = useState('');
    const [pw2, setPw2] = useState('');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);
    const [err, setErr] = useState(null);

    // The parent passes a new onLogout on every render; keep it in a ref so the
    // status is fetched once per token, not on every redraw (that used to throw
    // the reseller back from the code box to the email box).
    const onLogoutRef = useRef(onLogout);
    onLogoutRef.current = onLogout;
    const load = useCallback(async () => {
        const r = await call('/credits-pool/security/status', { token, method: 'GET' });
        if (r.status === 401) { onLogoutRef.current && onLogoutRef.current(); return; }
        if (r.status === 403 && r.body.weakPasswordLocked) { setStatus({ setupRequired: true, locked: r.body.error }); return; }
        if (!r.ok) { setStatus({ setupRequired: false }); return; }   // never lock someone out on a glitch; the server still enforces
        setStatus(r.body);
        // Only move forward: never undo a step the reseller is already on.
        setStep(cur => r.body.emailVerified ? 'password' : (cur === 'code' ? 'code' : 'email'));
    }, [token]);
    useEffect(() => { load(); }, [load]);

    if (!status) return <div className="login-container"><div className="login-card" style={{ textAlign: 'center' }}>Loading…</div></div>;
    if (!status.setupRequired) return children;
    if (status.locked) return (
        <div className="login-container"><div className="login-card" style={box}>
            <div className="login-header"><h1><Icon name="lock" size={22} /> Account locked</h1></div>
            <div className="alert alert-error"><span><Icon name="alert" size={16} /></span><span>{status.locked}</span></div>
            <button type="button" className="btn btn-secondary" style={{ width: '100%' }} onClick={() => { localStorage.removeItem('cpResellerToken'); localStorage.removeItem('cpResellerInfo'); onLogoutRef.current && onLogoutRef.current(); }}>Log out</button>
        </div></div>
    );

    const name = status.username || username;
    const run = async (fn) => { setBusy(true); setErr(null); setMsg(null); try { await fn(); } finally { setBusy(false); } };

    const sendEmailCode = () => run(async () => {
        const r = await call('/credits-pool/security/email/send', { token, body: { email } });
        if (!r.ok) return setErr(r.body.error || 'Could not send the code.');
        setCode(''); setStep('code'); setMsg(`We sent a 6-digit code to ${r.body.sentTo}. Check your inbox (and spam).`);
    });
    const verifyEmail = () => run(async () => {
        const r = await call('/credits-pool/security/email/verify', { token, body: { code } });
        if (!r.ok) return setErr(r.body.error || 'Wrong code.');
        setStatus(s => ({ ...s, ...r.body }));
        setCode(''); setStep('password');
        setMsg(r.body.resetCodeSent ? `Email verified ✓ — a new code was sent to ${r.body.emailMasked} to set your password.` : 'Email verified ✓ — now press "Send code" to set your password.');
    });
    const sendResetCode = () => run(async () => {
        const r = await call('/credits-pool/security/password/send', { token });
        if (!r.ok) return setErr(r.body.error || 'Could not send the code.');
        setMsg(`We sent a 6-digit code to ${r.body.sentTo}.`);
    });
    const savePassword = () => run(async () => {
        if (pw !== pw2) return setErr('The two passwords do not match.');
        const r = await call('/credits-pool/security/password/reset', { token, body: { code, newPassword: pw } });
        if (!r.ok) return setErr(r.body.error || 'Could not change the password.');
        localStorage.setItem('cpResellerToken', r.body.token);
        onToken && onToken(r.body.token);
        setStatus(s => ({ ...s, ...r.body, setupRequired: false }));
    });

    const checksOk = passwordChecks(name, pw).every(c => c.ok) && pw === pw2;

    return (
        <div className="login-container">
            <div className="login-card" style={box}>
                <div className="login-header">
                    <h1><Icon name="lock" size={22} /> Secure your account</h1>
                    <p>Account <b>{name}</b> — required before you can use the panel</p>
                </div>
                <div style={{ display: 'flex', gap: 6, marginBottom: 14, fontSize: 12 }}>
                    <span style={{ flex: 1, textAlign: 'center', padding: 6, borderRadius: 6, background: status.emailVerified ? '#dcfce7' : '#dbeafe', color: status.emailVerified ? '#166534' : '#1e40af', fontWeight: 600 }}>1. Verify email {status.emailVerified ? '✓' : ''}</span>
                    <span style={{ flex: 1, textAlign: 'center', padding: 6, borderRadius: 6, background: status.emailVerified ? '#dbeafe' : '#f1f5f9', color: status.emailVerified ? '#1e40af' : '#94a3b8', fontWeight: 600 }}>2. New password</span>
                </div>
                {msg && <div style={note('#f0fdf4', '#166534')}>{msg}</div>}
                {err && <div className="alert alert-error"><span><Icon name="alert" size={16} /></span><span>{err}</span></div>}

                {step === 'email' && (
                    <form onSubmit={e => { e.preventDefault(); sendEmailCode(); }}>
                        <div className="form-group">
                            <label className="form-label">Your email address</label>
                            <input type="email" className="form-input" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required autoFocus />
                            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Used to verify you and to reset your password if you forget it.</div>
                        </div>
                        <button className="btn btn-primary" style={{ width: '100%' }} disabled={busy}>{busy ? 'Sending…' : 'Send verification code'}</button>
                    </form>
                )}

                {step === 'code' && (
                    <form onSubmit={e => { e.preventDefault(); verifyEmail(); }}>
                        <div className="form-group">
                            <label className="form-label">6-digit code</label>
                            <input className="form-input" inputMode="numeric" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                                placeholder="123456" required autoFocus style={{ letterSpacing: 6, fontSize: 20, textAlign: 'center' }} />
                        </div>
                        <button className="btn btn-primary" style={{ width: '100%' }} disabled={busy || code.length !== 6}>{busy ? 'Checking…' : 'Verify email'}</button>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, fontSize: 13 }}>
                            <button type="button" className="btn btn-secondary" style={{ padding: '4px 10px' }} onClick={() => { setStep('email'); setMsg(null); setErr(null); }}>Change email</button>
                            <button type="button" className="btn btn-secondary" style={{ padding: '4px 10px' }} disabled={busy} onClick={sendEmailCode}>Resend code</button>
                        </div>
                    </form>
                )}

                {step === 'password' && (
                    <form onSubmit={e => { e.preventDefault(); savePassword(); }}>
                        <div className="form-group">
                            <label className="form-label">Code sent to {status.emailMasked}</label>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <input className="form-input" inputMode="numeric" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                                    placeholder="123456" required style={{ letterSpacing: 6, fontSize: 18, textAlign: 'center' }} />
                                <button type="button" className="btn btn-secondary" disabled={busy} onClick={sendResetCode} style={{ whiteSpace: 'nowrap' }}>Send code</button>
                            </div>
                        </div>
                        <div className="form-group">
                            <label className="form-label">New password</label>
                            <input type="password" className="form-input" value={pw} onChange={e => setPw(e.target.value)} autoComplete="new-password" required />
                            <Rules username={name} password={pw} />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Repeat new password</label>
                            <input type="password" className="form-input" value={pw2} onChange={e => setPw2(e.target.value)} autoComplete="new-password" required />
                            {pw2 && pw !== pw2 && <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>The passwords do not match.</div>}
                        </div>
                        <button className="btn btn-primary" style={{ width: '100%' }} disabled={busy || code.length !== 6 || !checksOk}>{busy ? 'Saving…' : 'Save new password'}</button>
                    </form>
                )}

                <div style={{ textAlign: 'center', marginTop: 16 }}>
                    <button type="button" className="btn btn-secondary" style={{ padding: '4px 12px', fontSize: 13 }} onClick={() => { localStorage.removeItem('cpResellerToken'); localStorage.removeItem('cpResellerInfo'); onLogout && onLogout(); }}>Log out</button>
                </div>
            </div>
        </div>
    );
}

/** "Forgot password" on the login page: account name -> code to the linked email -> new password. */
export function ForgotPassword({ onBack }) {
    const [email, setEmail] = useState('');
    const [sent, setSent] = useState(false);
    const [code, setCode] = useState('');
    const [pw, setPw] = useState('');
    const [pw2, setPw2] = useState('');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);
    const [err, setErr] = useState(null);
    const [done, setDone] = useState(false);

    const send = async () => {
        setBusy(true); setErr(null);
        const r = await call('/credits-pool/forgot-password/send', { body: { email } });
        setBusy(false);
        if (!r.ok) return setErr(r.body.error || 'Could not send the code.');
        setSent(true); setMsg(r.body.message);
    };
    const reset = async () => {
        if (pw !== pw2) return setErr('The two passwords do not match.');
        setBusy(true); setErr(null);
        const r = await call('/credits-pool/forgot-password/reset', { body: { email, code, newPassword: pw } });
        setBusy(false);
        if (!r.ok) return setErr(r.body.error || 'Could not change the password.');
        setDone(true); setMsg(r.body.message);
    };
    const ok = passwordChecks('', pw).every(c => c.ok) && pw === pw2;

    return (
        <div>
            <h3 style={{ margin: '0 0 6px' }}>Forgot password</h3>
            {msg && <div style={note(done ? '#f0fdf4' : '#eff6ff', done ? '#166534' : '#1e40af')}>{msg}</div>}
            {err && <div className="alert alert-error"><span><Icon name="alert" size={16} /></span><span>{err}</span></div>}
            {done ? (
                <button className="btn btn-primary" style={{ width: '100%' }} onClick={onBack}>Back to login</button>
            ) : !sent ? (
                <form onSubmit={e => { e.preventDefault(); send(); }}>
                    <div className="form-group">
                        <label className="form-label">Email</label>
                        <input type="email" className="form-input" value={email} onChange={e => setEmail(e.target.value)} placeholder="The email linked to your account" autoComplete="email" required autoFocus />
                    </div>
                    <button className="btn btn-primary" style={{ width: '100%' }} disabled={busy}>{busy ? 'Sending…' : 'Send code to my email'}</button>
                </form>
            ) : (
                <form onSubmit={e => { e.preventDefault(); reset(); }}>
                    <div className="form-group">
                        <label className="form-label">6-digit code</label>
                        <input className="form-input" inputMode="numeric" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                            placeholder="123456" required autoFocus style={{ letterSpacing: 6, fontSize: 18, textAlign: 'center' }} />
                    </div>
                    <div className="form-group">
                        <label className="form-label">New password</label>
                        <input type="password" className="form-input" value={pw} onChange={e => setPw(e.target.value)} autoComplete="new-password" required />
                        <Rules username={null} password={pw} />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Repeat new password</label>
                        <input type="password" className="form-input" value={pw2} onChange={e => setPw2(e.target.value)} autoComplete="new-password" required />
                    </div>
                    <button className="btn btn-primary" style={{ width: '100%' }} disabled={busy || code.length !== 6 || !ok}>{busy ? 'Saving…' : 'Set new password'}</button>
                    <button type="button" className="btn btn-secondary" style={{ width: '100%', marginTop: 8 }} disabled={busy} onClick={send}>Resend code</button>
                </form>
            )}
            {!done && <div style={{ textAlign: 'center', marginTop: 14 }}><button type="button" className="btn btn-secondary" style={{ padding: '4px 12px', fontSize: 13 }} onClick={onBack}>← Back to login</button></div>}
        </div>
    );
}
